import ApiWithLogging from '../utils/apiWithLogging';
import {
  Quotation,
  QuotationForm,
  QuotationResponse,
  QuotationSummary,
  GetQuotationsParams,
  GetQuotationSummaryParams,
  QuotationCancelRequest,
  QuotationExtendRequest,
  QuotationDuplicateRequest,
  QuotationPreparationPrefill,
  QuotationPreparationJobRequest,
  QuotationProductOption,
  QuotationInvoicePrefill,
  QuotationInvoiceCreateRequest
} from '../types/quotation';
import type { Invoice } from '../types/invoice';
import type { PreparationJobActionResponse } from './preparationJobService';

const API_BASE = '/quotations';

class QuotationService {
  /**
   * ดึงรายการใบเสนอราคาทั้งหมด
   */
  async getQuotations(params?: GetQuotationsParams): Promise<QuotationResponse> {
    return await ApiWithLogging.get<QuotationResponse>(`${API_BASE}/`, { params });
  }

  /**
   * ดึงข้อมูลใบเสนอราคาตาม ID
   */
  async getQuotationById(id: number): Promise<Quotation> {
    return await ApiWithLogging.get<Quotation>(`${API_BASE}/${id}`);
  }

  /**
   * สร้างใบเสนอราคาใหม่
   */
  async createQuotation(data: QuotationForm): Promise<Quotation> {
    return await ApiWithLogging.post<Quotation>(`${API_BASE}/`, data);
  }

  /**
   * อัพเดทข้อมูลใบเสนอราคา
   */
  async updateQuotation(id: number, data: Partial<QuotationForm>): Promise<Quotation> {
    return await ApiWithLogging.put<Quotation>(`${API_BASE}/${id}`, data);
  }

  /**
   * ลบใบเสนอราคา
   */
  async deleteQuotation(id: number): Promise<void> {
    await ApiWithLogging.delete(`${API_BASE}/${id}`);
  }

  /**
   * ส่งใบเสนอราคาให้ลูกค้า
   */
  async sendQuotation(id: number, data?: { sent_via?: string; sent_to_email?: string | null }): Promise<Quotation> {
    return await ApiWithLogging.post<Quotation>(`${API_BASE}/${id}/send`, data ?? {});
  }

  /**
   * ยืนยันการรับใบเสนอราคา (ACCEPTED)
   */
  async acceptQuotation(id: number): Promise<Quotation> {
    return await ApiWithLogging.post<Quotation>(`${API_BASE}/${id}/accept`, {});
  }

  /**
   * ปฏิเสธใบเสนอราคา (REJECTED)
   */
  async rejectQuotation(id: number, reason: string): Promise<Quotation> {
    return await ApiWithLogging.post<Quotation>(`${API_BASE}/${id}/reject`, { reason });
  }

  /**
   * ดาวน์โหลด PDF ใบเสนอราคา
   */
  async downloadQuotationPDF(id: number): Promise<Blob> {
    return await ApiWithLogging.get(`${API_BASE}/${id}/pdf`, {
      responseType: 'blob'
    });
  }

  /**
   * ยกเลิกใบเสนอราคา (→ CANCELLED) — ต้องมีเหตุผล
   */
  async cancelQuotation(id: number, data: QuotationCancelRequest): Promise<Quotation> {
    return await ApiWithLogging.post<Quotation>(`${API_BASE}/${id}/cancel`, data);
  }

  /**
   * ต่ออายุ (เลื่อนวันมีผลถึง) — valid_until YYYY-MM-DD
   */
  async extendQuotation(id: number, data: QuotationExtendRequest): Promise<Quotation> {
    return await ApiWithLogging.post<Quotation>(`${API_BASE}/${id}/extend`, data);
  }

  /**
   * สร้างสำเนาใบเสนอราคา — `supersede: true` = ออกใบใหม่แทนใบเดิม (ใบเดิม → SUPERSEDED)
   * ไม่ส่ง body = สำเนาธรรมดา (ใบเดิมคงสถานะ)
   */
  async duplicateQuotation(id: number, data?: QuotationDuplicateRequest): Promise<Quotation> {
    return await ApiWithLogging.post<Quotation>(`${API_BASE}/${id}/duplicate`, data ?? {});
  }

  /**
   * Export ใบเสนอราคาเป็น Excel
   */
  async exportQuotations(params?: GetQuotationsParams): Promise<Blob> {
    return await ApiWithLogging.get(`${API_BASE}/export/`, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * ตัวเลือกสินค้าสำหรับช่อง "สินค้า" ต่อรายการในฟอร์มใบเสนอราคา
   * ยิง endpoint ของโมดูลนี้เอง (gate สิทธิ์ใบเสนอราคา) — **ห้ามยิง /products/ หรือ /preparation-jobs/products**
   * เพราะเซล/บัญชีบน prod ไม่มี products.view / preparation_jobs.*
   */
  async getProductOptions(): Promise<QuotationProductOption[]> {
    const res = await ApiWithLogging.get<{ items: QuotationProductOption[] }>(`${API_BASE}/products`);
    return res.items || [];
  }

  /**
   * ข้อมูลตั้งต้นสำหรับเปิดใบงานเตรียมเครื่องจากใบเสนอราคา (สินค้าที่ระบบเดาให้ · โครงการ · วันที่ ฯลฯ)
   * รวมธง `can_create`/`blocked_reason`/`existing_job` — FE ต้องเชื่อ backend ไม่ตัดสินเอง
   */
  async getPreparationPrefill(id: number): Promise<QuotationPreparationPrefill> {
    return await ApiWithLogging.get<QuotationPreparationPrefill>(`${API_BASE}/${id}/preparation-job-prefill`);
  }

  /**
   * เปิดใบงานเตรียมเครื่องจากใบเสนอราคา — response รูปเดียวกับ POST /preparation-jobs (`{job, warnings}`)
   * error: 400 สถานะใบเสนอราคาไม่อนุญาต · 409 มีใบงานที่ยังมีชีวิตอยู่แล้ว · 403 ไม่มี preparation_jobs.create
   */
  async createPreparationJob(id: number, data: QuotationPreparationJobRequest): Promise<PreparationJobActionResponse> {
    return await ApiWithLogging.post<PreparationJobActionResponse>(`${API_BASE}/${id}/preparation-job`, data);
  }

  /**
   * FB-2026-0018: ข้อมูลตั้งต้นของโมดัล "สร้างใบแจ้งหนี้" — แถว + ยอดของชุดที่เลือก (backend คิดด้วยสูตรเดียวกับใบจริง)
   * `itemIds` ไม่ส่ง = ชุดที่ติ๊กไว้ก่อน (แถวมัดจำ)
   */
  async getInvoicePrefill(id: number, itemIds?: number[], withholdingTaxRate?: number): Promise<QuotationInvoicePrefill> {
    const params: Record<string, string | number> = {};
    if (itemIds) params.item_ids = itemIds.join(',');
    if (withholdingTaxRate) params.withholding_tax_rate = withholdingTaxRate;
    return await ApiWithLogging.get<QuotationInvoicePrefill>(`${API_BASE}/${id}/invoice-prefill`, { params });
  }

  /**
   * FB-2026-0018: สร้างใบแจ้งหนี้รายการเดียว (ONE_OFF · DRAFT) จากแถวที่เลือก
   * error: 400 สถานะใบเสนอราคาไม่อนุญาต/แถวเลือกไม่ได้ · 409 มีใบแจ้งหนี้จากใบนี้แล้ว · 403 ไม่มี invoices.create
   */
  async createInvoice(id: number, data: QuotationInvoiceCreateRequest): Promise<Invoice> {
    return await ApiWithLogging.post<Invoice>(`${API_BASE}/${id}/invoice`, data);
  }

  /**
   * ดึงสรุปข้อมูลใบเสนอราคา — รับ filter ชุดเดียวกับ list (ยกเว้น page/limit/sort)
   * response: `total_amount` = pipeline (DRAFT/SENT/ACCEPTED) · `converted_amount` = ใบที่แปลงแล้ว
   */
  async getQuotationSummary(params?: GetQuotationSummaryParams): Promise<QuotationSummary> {
    return await ApiWithLogging.get<QuotationSummary>(`${API_BASE}/summary`, { params });
  }
}

export default new QuotationService();
